package cz.lhoracek.mapl.ui.screens.home

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.intl.Locale
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import cz.lhoracek.mapl.data.model.Session
import cz.lhoracek.mapl.data.model.SessionStatus
import cz.lhoracek.mapl.ui.components.CommitPendingDeleteOnDispose
import cz.lhoracek.mapl.ui.components.DismissibleEndToStartItem
import cz.lhoracek.mapl.ui.components.DeleteDismissBackground
import cz.lhoracek.mapl.ui.components.MaplTopAppBar
import cz.lhoracek.mapl.ui.components.QrScannerView
import cz.lhoracek.mapl.ui.components.UndoDeleteSnackbarEffect
import cz.lhoracek.mapl.ui.screens.home.JoinResult
import cz.lhoracek.mapl.ui.theme.MaplTheme
import mapl.composeapp.generated.resources.*
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun HomeScreen(
    onNewSession: () -> Unit,
    onOpenSession: (String) -> Unit,
    onOpenRules: () -> Unit,
    showLanguageSelector: Boolean = false,
    selectedLanguageCode: String? = null,
    onSelectLanguageCode: (String) -> Unit = { _ -> },
) {
    val viewModel: HomeViewModel = koinViewModel()
    val sessions by viewModel.sessions.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val joinResult by viewModel.joinResult.collectAsState()
    val pendingDeleteCount by viewModel.pendingDeleteCount.collectAsState()
    val pendingDeleteSnackbarToken by viewModel.pendingDeleteSnackbarToken.collectAsState()
    val undoSessionIds by viewModel.undoSessionIds.collectAsState()

    HomeScreenContent(
        sessions = sessions,
        isLoading = isLoading,
        joinResult = joinResult,
        onNewSession = onNewSession,
        onOpenSession = onOpenSession,
        onOpenRules = onOpenRules,
        showLanguageSelector = showLanguageSelector,
        selectedLanguageCode = selectedLanguageCode,
        onSelectLanguageCode = onSelectLanguageCode,
        onJoinSession = viewModel::joinSession,
        onJoinByUrl = viewModel::joinByUrl,
        onEndSession = viewModel::endSession,
        pendingDeleteCount = pendingDeleteCount,
        pendingDeleteSnackbarToken = pendingDeleteSnackbarToken,
        undoSessionIds = undoSessionIds,
        onMarkForDelete = viewModel::markForDelete,
        onCancelDelete = viewModel::cancelDelete,
        onCommitDelete = viewModel::commitDelete,
        onClearUndoSignal = viewModel::clearUndoSignal,
        onConsumeJoinResult = viewModel::consumeJoinResult,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeScreenContent(
    sessions: List<HomeSessionItem>,
    isLoading: Boolean,
    joinResult: JoinResult?,
    onNewSession: () -> Unit,
    onOpenSession: (String) -> Unit,
    onOpenRules: () -> Unit,
    showLanguageSelector: Boolean,
    selectedLanguageCode: String?,
    onSelectLanguageCode: (String) -> Unit,
    onJoinSession: (String) -> Unit,
    onJoinByUrl: (String) -> Unit,
    onEndSession: (String) -> Unit,
    pendingDeleteCount: Int,
    pendingDeleteSnackbarToken: Int,
    undoSessionIds: Set<String>,
    onMarkForDelete: (HomeSessionItem) -> Unit,
    onCancelDelete: () -> Unit,
    onCommitDelete: () -> Unit,
    onClearUndoSignal: (String) -> Unit,
    onConsumeJoinResult: () -> Unit,
) {
    var sessionToEnd by remember { mutableStateOf<HomeSessionItem?>(null) }
    var showJoinDialog by remember { mutableStateOf(false) }
    var showLanguageMenu by remember { mutableStateOf(false) }
    var joinInput by remember { mutableStateOf("") }
    val snackbarHostState = remember { SnackbarHostState() }
    val sessionDeletedMessage = stringResource(Res.string.home_session_deleted)
    val sessionsDeletedMessage = stringResource(Res.string.home_sessions_deleted_count, pendingDeleteCount)
    val undoLabel = stringResource(Res.string.undo)
    val effectiveLanguageCode = resolveLanguageCode(selectedLanguageCode, Locale.current.language)
    val languageButtonLabel = effectiveLanguageCode.uppercase()

    UndoDeleteSnackbarEffect(
        trigger = pendingDeleteSnackbarToken,
        enabled = pendingDeleteCount > 0,
        snackbarHostState = snackbarHostState,
        message = if (pendingDeleteCount == 1) sessionDeletedMessage else sessionsDeletedMessage,
        actionLabel = undoLabel,
        dismissCurrentBeforeShowing = true,
        onUndo = onCancelDelete,
    )
    CommitPendingDeleteOnDispose(onCommitDelete)

    // React to join result
    val result = joinResult
    if (result is JoinResult.Success) {
        val joinedItem = sessions.firstOrNull { it.session.id == result.sessionId }
        if (joinedItem != null) {
            onConsumeJoinResult()
            if (!joinedItem.isPendingSync) {
                onOpenSession(result.sessionId)
            }
        }
    }

    if (showJoinDialog) {
        AlertDialog(
            onDismissRequest = {
                showJoinDialog = false
                joinInput = ""
                onConsumeJoinResult()
            },
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text(stringResource(Res.string.home_join_game)) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    // Camera scanner
                    QrScannerView(
                        onResult = { url ->
                            showJoinDialog = false
                            joinInput = ""
                            onJoinByUrl(url)
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(220.dp)
                            .clip(MaterialTheme.shapes.medium),
                    )
                    HorizontalDivider()
                    Text(
                        stringResource(Res.string.home_join_manual),
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    OutlinedTextField(
                        value = joinInput,
                        onValueChange = { joinInput = it; onConsumeJoinResult() },
                        label = { Text(stringResource(Res.string.home_session_id)) },
                        isError = joinResult is JoinResult.NotFound,
                        supportingText = if (joinResult is JoinResult.NotFound) {
                            { Text(stringResource(Res.string.home_session_not_found), color = MaterialTheme.colorScheme.error) }
                        } else null,
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            },
            confirmButton = {
                TextButton(
                    onClick = { onJoinSession(joinInput) },
                    enabled = joinInput.isNotBlank(),
                ) { Text(stringResource(Res.string.home_join)) }
            },
            dismissButton = {
                TextButton(onClick = {
                    showJoinDialog = false
                    joinInput = ""
                    onConsumeJoinResult()
                }) { Text(stringResource(Res.string.cancel)) }
            },
        )
    }

    val endingSession = sessionToEnd
    if (endingSession != null) {
        AlertDialog(
            onDismissRequest = { sessionToEnd = null },
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text(stringResource(Res.string.session_end_title)) },
            text = { Text(stringResource(Res.string.session_end_confirm_name, endingSession.session.name)) },
            confirmButton = {
                TextButton(onClick = {
                    onEndSession(endingSession.session.id)
                    sessionToEnd = null
                }) { Text(stringResource(Res.string.end)) }
            },
            dismissButton = {
                TextButton(onClick = { sessionToEnd = null }) { Text(stringResource(Res.string.cancel)) }
            },
        )
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            MaplTopAppBar(
                title = stringResource(Res.string.home_title),
                actions = {
                    if (showLanguageSelector) {
                        Box {
                            IconButton(onClick = { showLanguageMenu = true }) {
                                Text(
                                    text = languageButtonLabel,
                                    style = MaterialTheme.typography.labelMedium,
                                    fontWeight = FontWeight.Bold,
                                )
                            }
                            DropdownMenu(
                                expanded = showLanguageMenu,
                                onDismissRequest = { showLanguageMenu = false },
                            ) {
                                DropdownMenuItem(
                                    text = { Text(stringResource(Res.string.home_language_czech)) },
                                    onClick = {
                                        showLanguageMenu = false
                                        onSelectLanguageCode("cs")
                                    },
                                )
                                DropdownMenuItem(
                                    text = { Text(stringResource(Res.string.home_language_english)) },
                                    onClick = {
                                        showLanguageMenu = false
                                        onSelectLanguageCode("en")
                                    },
                                )
                            }
                        }
                    }
                    IconButton(onClick = onOpenRules) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_info),
                            contentDescription = stringResource(Res.string.home_rules_title)
                        )
                    }
                },
            )
        },
        floatingActionButton = {
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                FloatingActionButton(
                    onClick = { showJoinDialog = true },
                    containerColor = MaterialTheme.colorScheme.secondaryContainer,
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.ic_qr_code),
                        contentDescription = stringResource(Res.string.home_join_game),
                    )
                }
                FloatingActionButton(
                    onClick = onNewSession,
                    containerColor = MaterialTheme.colorScheme.primary,
                ) {
                    Text("+", style = MaterialTheme.typography.titleLarge)
                }
            }
        },
    ) { innerPadding ->
        when {
            isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(innerPadding),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }
            sessions.isEmpty() -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(innerPadding),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        stringResource(Res.string.home_no_sessions),
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(innerPadding),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    items(sessions, key = { it.session.id }) { item ->
                        val session = item.session
                        DismissibleEndToStartItem(
                            resetSignal = undoSessionIds.contains(session.id),
                            onResetHandled = { onClearUndoSignal(session.id) },
                            onDismissed = { onMarkForDelete(item) },
                            backgroundContent = {
                                DeleteDismissBackground(
                                    modifier = Modifier.clip(MaterialTheme.shapes.medium),
                                )
                            },
                        ) {
                            SessionCard(
                                session = session,
                                isPendingSync = item.isPendingSync,
                                roundsCount = item.roundsCount,
                                onClick = {
                                    if (!item.isPendingSync) onOpenSession(session.id)
                                },
                                onLongClick = if (session.status == SessionStatus.ACTIVE && !item.isPendingSync) {
                                    { sessionToEnd = item }
                                } else {
                                    {}
                                },
                            )
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SessionCard(
    session: Session,
    roundsCount: Int,
    isPendingSync: Boolean = false,
    onClick: () -> Unit,
    onLongClick: () -> Unit,
) {
    val isFinished = session.status == SessionStatus.FINISHED || isPendingSync
    Card(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = if (isFinished)
                MaterialTheme.colorScheme.surfaceVariant
            else
                MaterialTheme.colorScheme.surface,
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (isFinished) 0.dp else 2.dp),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = if (isPendingSync) {
                        stringResource(Res.string.home_pending_session_name, session.id)
                    } else {
                        session.name
                    },
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1
                )
                Spacer(Modifier.height(2.dp))
                Text(
                    text = if (isPendingSync) {
                        stringResource(Res.string.home_pending_session_subtitle)
                    } else {
                        stringResource(
                            Res.string.home_players_rounds_count,
                            session.playerIds.size,
                            roundsCount,
                            session.currentBet,
                        )
                    },
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            if (isPendingSync) {
                Text(
                    stringResource(Res.string.home_status_waiting_sync),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else if (isFinished) {
                Text(
                    stringResource(Res.string.home_status_finished),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                Text(
                    stringResource(Res.string.home_status_active),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.primary,
                    fontWeight = FontWeight.Bold,
                )
            }
        }
    }
}

@Preview
@Composable
private fun HomeScreenPreview() {
    MaplTheme {
        HomeScreenContent(
            sessions = listOf(
                HomeSessionItem(
                    session = Session(
                        id = "1",
                        name = "Páteční mariáš",
                        baseBet = 1,
                        currentBet = 2,
                        status = SessionStatus.ACTIVE,
                        playerIds = listOf("1", "2", "3")
                    ),
                    roundsCount = 8,
                ),
                HomeSessionItem(
                    session = Session(
                        id = "2",
                        name = "Včerejší hra",
                        baseBet = 1,
                        currentBet = 1,
                        status = SessionStatus.FINISHED,
                        playerIds = listOf("1", "2", "3", "4")
                    ),
                    roundsCount = 13,
                ),
                HomeSessionItem(
                    session = Session(
                        id = "9f4e84f0-7c80-4f70-b8e4-2dc8b95ef3d6",
                        name = "9f4e84f0-7c80-4f70-b8e4-2dc8b95ef3d6",
                        baseBet = 0,
                        currentBet = 0,
                        status = SessionStatus.FINISHED,
                        playerIds = emptyList(),
                    ),
                    roundsCount = 0,
                    isPendingSync = true,
                )
            ),
            isLoading = false,
            joinResult = null,
            onNewSession = {},
            onOpenSession = {},
            onOpenRules = {},
            showLanguageSelector = true,
            selectedLanguageCode = null,
            onSelectLanguageCode = { _ -> },
            onJoinSession = {},
            onJoinByUrl = {},
            onEndSession = {},
            pendingDeleteCount = 0,
            pendingDeleteSnackbarToken = 0,
            undoSessionIds = emptySet(),
            onMarkForDelete = {},
            onCancelDelete = {},
            onCommitDelete = {},
            onClearUndoSignal = { _ -> },
            onConsumeJoinResult = {}
        )
    }
}

private fun resolveLanguageCode(selectedLanguageCode: String?, localeLanguageCode: String): String {
    return normalizeLanguageCode(selectedLanguageCode)
        ?: normalizeLanguageCode(localeLanguageCode)
        ?: "en"
}

private fun normalizeLanguageCode(value: String?): String? {
    val normalized = value?.trim()?.lowercase() ?: return null
    return when {
        normalized.startsWith("cs") -> "cs"
        normalized.startsWith("en") -> "en"
        else -> null
    }
}
